import { useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, Box,
  Table, TableHead, TableRow, TableCell, TableBody, Alert, FormControlLabel, Checkbox,
  Chip, IconButton, Tooltip, LinearProgress,
} from '@mui/material';
import { Download, UploadFile, Close, ContentCopy } from '@mui/icons-material';
import type { BulkResult } from '../api/bulkOnboard.api';

export interface BulkColumn {
  /** Header label written to the CSV — MUST match what the backend maps (e.g. "Dealer Name"). */
  header: string;
  /** Example value placed in the downloadable template's sample row. */
  sample: string;
  required?: boolean;
}

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  /** "dealer" | "client" — used only for labels. */
  entityLabel: string;
  columns: BulkColumn[];
  /** Sends parsed rows to the API and returns the import result. */
  onImport: (rows: Record<string, string>[], createLogins: boolean) => Promise<BulkResult>;
  onDone?: () => void;
  /** Show the "Create login accounts" toggle (dealer/client onboarding only). */
  showCreateLogins?: boolean;
}

/** Minimal RFC-4180-ish CSV parser (handles quoted fields, commas & newlines in quotes). */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 1; }
        else inQuotes = false;
      } else field += ch;
    } else if (ch === '"') inQuotes = true;
    else if (ch === ',') { row.push(field); field = ''; }
    else if (ch === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
    else if (ch === '\r') { /* ignore */ }
    else field += ch;
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim().length > 0));
}

export function BulkImportDialog({ open, onClose, title, entityLabel, columns, onImport, onDone, showCreateLogins = true }: Props) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [createLogins, setCreateLogins] = useState(true);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<BulkResult | null>(null);

  const reset = () => {
    setRows([]); setFileName(''); setParseError(null); setResult(null); setImporting(false);
  };
  const close = () => { reset(); onClose(); };

  const downloadTemplate = () => {
    const headers = columns.map((c) => c.header);
    const sample = columns.map((c) => (c.sample.includes(',') ? `"${c.sample}"` : c.sample));
    const csv = `${headers.join(',')}\n${sample.join(',')}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${entityLabel.toLowerCase()}-bulk-template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = (file: File) => {
    setParseError(null); setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const grid = parseCsv(String(reader.result ?? ''));
        if (grid.length < 2) throw new Error('The file has no data rows.');
        const headers = grid[0].map((h) => h.trim());
        const parsed = grid.slice(1).map((r) => {
          const obj: Record<string, string> = {};
          headers.forEach((h, idx) => { obj[h] = (r[idx] ?? '').trim(); });
          return obj;
        });
        setRows(parsed);
        setFileName(file.name);
      } catch (e) {
        setParseError((e as Error).message);
        setRows([]);
      }
    };
    reader.readAsText(file);
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const res = await onImport(rows, createLogins);
      setResult(res);
      onDone?.();
    } catch (e) {
      setParseError((e as Error).message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const credentials = result?.results.filter((r) => r.temporaryPassword) ?? [];

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>
        {title}
        <IconButton onClick={close} sx={{ position: 'absolute', right: 8, top: 8 }}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {!result && (
          <Stack spacing={2}>
            <Alert severity="info">
              Download the template, fill one {entityLabel.toLowerCase()} per row, and upload it. Only the name is
              required — everything else can be completed later. <b>Region/State</b> may be the name or its id.
            </Alert>
            <Stack direction="row" spacing={1}>
              <Button startIcon={<Download />} variant="outlined" onClick={downloadTemplate}>Download template (CSV)</Button>
              <Button startIcon={<UploadFile />} variant="contained" onClick={() => fileInput.current?.click()}>Choose CSV file</Button>
              <input ref={fileInput} type="file" accept=".csv,text/csv" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }} />
            </Stack>
            {fileName && <Typography variant="body2" color="text.secondary">Loaded <b>{fileName}</b> — {rows.length} row(s).</Typography>}
            {parseError && <Alert severity="error">{parseError}</Alert>}

            {rows.length > 0 && (
              <>
                {showCreateLogins && (
                  <FormControlLabel
                    control={<Checkbox checked={createLogins} onChange={(e) => setCreateLogins(e.target.checked)} />}
                    label={`Create login accounts (rows with an email get a portal login — default password Gig4ce@123)`}
                  />
                )}
                <Box sx={{ maxHeight: 280, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>{columns.map((c) => <TableCell key={c.header} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{c.header}</TableCell>)}</TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.slice(0, 50).map((r, i) => (
                        <TableRow key={i}>{columns.map((c) => <TableCell key={c.header} sx={{ whiteSpace: 'nowrap' }}>{r[c.header] ?? ''}</TableCell>)}</TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
                {rows.length > 50 && <Typography variant="caption" color="text.secondary">Showing first 50 of {rows.length} rows.</Typography>}
              </>
            )}
            {importing && <LinearProgress />}
          </Stack>
        )}

        {result && (
          <Stack spacing={2}>
            <Alert severity={result.failed === 0 ? 'success' : 'warning'}>
              Imported <b>{result.created}</b> of {result.total} {entityLabel.toLowerCase()}(s).
              {result.failed > 0 && <> {result.failed} failed.</>}
              {result.loginsCreated > 0 && <> {result.loginsCreated} login(s) created.</>}
            </Alert>

            {credentials.length > 0 && (
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                  <Typography variant="subtitle2">Login credentials (default password Gig4ce@123 — ask users to change it on first sign-in)</Typography>
                  <Tooltip title="Copy all">
                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(credentials.map((c) => `${c.loginEmail}\t${c.temporaryPassword}`).join('\n'))}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Email</TableCell><TableCell>Temp password</TableCell></TableRow></TableHead>
                    <TableBody>
                      {credentials.map((c) => (
                        <TableRow key={c.row}><TableCell>{c.name}</TableCell><TableCell>{c.loginEmail}</TableCell><TableCell><code>{c.temporaryPassword}</code></TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}

            {result.failed > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Errors</Typography>
                <Box sx={{ maxHeight: 180, overflow: 'auto' }}>
                  {result.results.filter((r) => r.outcome === 'error').map((r) => (
                    <Stack key={r.row} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                      <Chip size="small" color="error" label={`Row ${r.row}`} />
                      <Typography variant="body2">{r.name || '(no name)'} — {r.message}</Typography>
                    </Stack>
                  ))}
                </Box>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {!result ? (
          <>
            <Button onClick={close}>Cancel</Button>
            <Button variant="contained" disabled={rows.length === 0 || importing} onClick={runImport}>
              Import {rows.length > 0 ? `${rows.length} row(s)` : ''}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={close}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
